import { Range, Button, Flex, Input, Label, Span, SearchSelect } from '@fareye/ui'
import {
  ddMMMMYYYY,
  hhmmADoMMMYYYY,
  FaDownload,
  hhmmA,
  isEmpty,
  toTileCase,
  getValueFromNestedKey,
  startCase
} from '@fareye/utils'
import { parseVoltage, dateFormatter, useTranslation } from '@fareye/common'
import React from 'react'
import { style } from '../../../style/styled'
import { labelColor } from '../../../utility/colors'
import { tripDetailConstant } from '../../../utility/constants'
interface IHistoryListing {
  data: any
  dataLength: number
  onSelect: (id: any) => void
  id: { [key: string]: any }
  historySlider: any
  downloadCsv: any
  currHistorySlider: any
  setCurrHistorySlider: any
  config?: any
  onTrackingProviderChange?: (provider: string) => void
  trackingProviders?: string[]
  defaultTrackingProvider?: string
}

export const formatters: any = {
  startCase,
  dateFormatterTZ: dateFormatter
}

export const HistoryListing = ({
  id,
  data,
  dataLength,
  onSelect,
  downloadCsv,
  historySlider,
  currHistorySlider,
  setCurrHistorySlider,
  config = {},
  onTrackingProviderChange,
  trackingProviders = [],
  defaultTrackingProvider
}: IHistoryListing) => {
  const {
    extendedColumns = [],
    downloadCsvFields = ['vehicleNo', 'landmark', 'istTimestamp']
  } = config || {}
  const { t } = useTranslation()
  const [selectedTrackingProvider, setSelectedTrackingProvider] = React.useState<any>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  React.useEffect(() => {
    setIsSubmitting(false)
  }, [data])

  React.useEffect(() => {
    if (defaultTrackingProvider) {
      setSelectedTrackingProvider({
        label: toTileCase(defaultTrackingProvider),
        value: defaultTrackingProvider
      })
    }
  }, [defaultTrackingProvider])
  const safeMin = currHistorySlider?.min && isFinite(currHistorySlider.min) ? currHistorySlider.min : null
  const safeMax = currHistorySlider?.max && isFinite(currHistorySlider.max) ? currHistorySlider.max : null
  const [historyValue, setHistoryValue] = React.useState<any>({ min: safeMin, max: safeMax })

  React.useEffect(() => {
    if (currHistorySlider?.min && isFinite(currHistorySlider.min) && isFinite(currHistorySlider.max)) {
      setHistoryValue({ min: currHistorySlider.min, max: currHistorySlider.max })
    }
  }, [currHistorySlider])

  const handleTrackingProviderChange = (item: any) => {
    setSelectedTrackingProvider(item)
  }

  const handleSubmit = () => {
    if (onTrackingProviderChange) {
      setIsSubmitting(true)
      onTrackingProviderChange(selectedTrackingProvider?.value || '')
    }
  }

  const providerOptions = trackingProviders.map((provider: string) => ({
    label: toTileCase(provider),
    value: provider
  }))

  const dataDownload = (historyData: any, fields: string[]) => {
    downloadCsv(
      historyData.map((item: any) => {
        return {
          vehicleNo: item.vehicleNo,
          landmark: item.landmark,
          istTimestamp: `${hhmmA(item.istTimestamp)},${ddMMMMYYYY(
            item.istTimestamp
          )}`,
          loadsensorWeight: item.extraData?.weight
        }
      }),
      fields
    )
  }

  function getColumnsValue(column: any, history: any, index: number) {
    const { key, label, formatter } = column
    const value = getValueFromNestedKey(history, key)
    let formattedValue: any =
      formatter && formatters[formatter]
        ? (value && formatters[formatter](value)) || '-'
        : value ?? '-'
    switch (key) {
      case 'extraData.voltage':
        formattedValue = formattedValue === '-' ? null : formattedValue
        const loadsensor = getValueFromNestedKey(
          history,
          'extraData.loadsensor'
        )
        formattedValue = parseVoltage(loadsensor, formattedValue)
        break
      case 'extraData.weight':
        formattedValue = formattedValue === '-' ? null : formattedValue
        break;
    }
    return (
      <Span key={`${key}${index}`} style={{ flex: 1, paddingLeft: '20px' }}>
        {formattedValue}
      </Span>
    )
  }

  return (
    <Flex column p={2}>
      <Flex justifyContentSpaceBetween alignItemsCenter p={2}>
        <Label variant={'h6'} style={{ fontWeight: 700 }}>
          {tripDetailConstant.history}
        </Label>
        <Flex justifyContentFlexEnd alignItemsCenter>
          <SearchSelect
            options={providerOptions}
            value={selectedTrackingProvider}
            placeholder={t('Select Source')}
            onChange={(item: any) => handleTrackingProviderChange(item)}
            isSearchable={false}
            t={t}
            style={{
              wrapper: {
                width: '118px',
                marginRight: '6px'
              },
              control: {
                height: '30px',
                minHeight: '30px'
              },
              valueContainer: {
                justifyContent: 'center'
              },
              singleValue: {
                fontSize: '11px',
                textAlign: 'center'
              },
              placeholder: {
                width: '100%',
                textAlign: 'center',
                fontSize: '11px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'rgba(90,90,90,1)'
              },
              option: {
                fontSize: '11px',
                textAlign: 'center'
              },
              dropdownIndicator: {
                display: 'block',
                transform: 'rotate(0deg) !important'
              },
              indicatorSeparator: {
                display: 'none'
              }
            }}
          />
          <Button
            variant="primary s"
            onClick={handleSubmit}
            disabled={isSubmitting}
            isLoading={isSubmitting}
            style={{ marginRight: '6px', height: '30px', fontSize: '11px', padding: '4px 8px' }}
          >
            {t('Apply')}
          </Button>
        </Flex>
      </Flex>
      {dataLength > 0 && !isSubmitting && (
        <Flex p={2} column style={{ marginBottom: '20px', overflow: 'hidden' }}>
          {dataLength > 1 &&
            isFinite(historySlider.min) && isFinite(historySlider.max) &&
            historyValue?.min && isFinite(historyValue.min) &&
            historyValue?.max && isFinite(historyValue.max) && (
            <Range
              formatLabel={(value: any) => hhmmADoMMMYYYY(value)}
              maxValue={historySlider.max}
              minValue={historySlider.min}
              value={historyValue}
              onChange={(value: any) => {
                setHistoryValue(value)
              }}
              onChangeComplete={(value: any) => setCurrHistorySlider(value)}
            />
          )}
          {!isEmpty(data) && (
            <Flex justifyContentFlexEnd p={1}>
              <Button
                disabled={isEmpty(data)}
                variant="s"
                onClick={() =>
                  !isEmpty(data) && dataDownload(data, downloadCsvFields)
                }
              >
                <FaDownload style={style.downloadIcon} />
                Download as CSV
              </Button>
            </Flex>
          )}
        </Flex>
      )}
      {!isEmpty(data) && !isSubmitting && (
        <Flex justifyContentSpaceAround style={{ paddingBottom: 10 }}>
          <Span color={labelColor.normalText} style={{ width: 23 }} />
          <Span color={labelColor.normalText} style={{ flex: 3 }}>
            {tripDetailConstant.landmark}
          </Span>
          <Span color={labelColor.normalText} style={{ flex: 1 }}>
            {tripDetailConstant.source}
          </Span>
          <Span color={labelColor.normalText} style={{ width: 130 }}>
            {tripDetailConstant.time}
          </Span>
          {extendedColumns.map((column: any) => (
            <Span color={labelColor.normalText} style={{ flex: 1 }}>
              {startCase(column.label)}
            </Span>
          ))}
        </Flex>
      )}

      {isSubmitting ? (
        <Label
          style={{
            width: '100%',
            paddingTop: '10vmax',
            textAlign: 'center',
            display: 'block'
          }}
        >
          Fetching pings...
        </Label>
      ) : !isEmpty(data) ? (
        data.map((item: any, index: number) => (
          <Flex
            key={index}
            justifyContentSpaceBetween
            style={{ marginBottom: 10 }}
          >
            <Input
              type="radio"
              checked={
                `${item.vehicleNo}${item.istTimestamp}` === id.locationId
              }
              onChange={() => {
                onSelect({
                  locationId: `${item.vehicleNo}${item.istTimestamp}`
                })
              }}
            />
            <Span
              style={{ flex: 3 }}
              dangerouslySetInnerHTML={{
                __html:
                  item &&
                  item.landmark &&
                  item.landmark.split('/').join('<br/>')
              }}
            />

            <Span style={{ flex: 1, paddingLeft: '20px' }}>
              {item?.trackingMode ? toTileCase(item?.trackingMode) : ''}
            </Span>
            <Flex p={2} style={{ width: 125 }} justifyContentSpaceAround column>
              <Span>{hhmmA(item.istTimestamp)}</Span>
              <Span>{ddMMMMYYYY(item.istTimestamp)}</Span>
            </Flex>
            {extendedColumns.map((column: any) => {
              return getColumnsValue(column, item, index)
            })}
          </Flex>
        ))
      ) : (
        <Label
          style={{
            width: '100%',
            paddingTop: '10vmax',
            textAlign: 'center',
            display: 'block'
          }}
        >
          No Data
        </Label>
      )}
    </Flex>
  )
}
